// src/components/VendorsView.jsx
// Vendor & Third-Party Relationship Management
import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, Plus, Building2, Users, FileText, Search,
  Loader2, LogOut, Edit2, Trash2, X, ExternalLink, FolderPlus, 
  Phone, Mail, Calendar, AlertCircle, MessageSquare, UserPlus, Clock, Upload,
  ChevronDown, ChevronRight, RefreshCw
} from 'lucide-react';
import { useVendors, useVendorCategories, useVendor } from '../hooks/useSupabase';
import { useDisplayColors } from '../lib/ThemeContext';
import { useVendorFolderTemplates, getVendorFolderPaths } from './VendorFoldersTab';
import { ensureSharePointFolder, listSharePointFolder, supabase } from '../lib/supabase';

var STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-600' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  terminated: { label: 'Terminated', color: 'bg-red-100 text-red-700' },
};

export default function VendorsView({ organizationId, userProfile, pc, sc, isViewer, onBack, showToast, signOut }) {
  var colors = useDisplayColors(pc, sc);
  var displayPrimary = colors.displayPrimary;
  var displaySecondary = colors.displaySecondary;
  var displayNavy = colors.displayNavy;
  var displayGreen = colors.displayGreen;
  var vendorHook = useVendors(organizationId);
  var vendors = vendorHook.vendors;
  var loading = vendorHook.loading;
  var createVendor = vendorHook.createVendor;
  var deleteVendor = vendorHook.deleteVendor;
  var catHook = useVendorCategories(organizationId);
  var categories = catHook.categories;
  
  var _activeVendor = useState(null);
  var activeVendorId = _activeVendor[0];
  var setActiveVendorId = _activeVendor[1];
  var _showNew = useState(false);
  var showNewVendor = _showNew[0];
  var setShowNewVendor = _showNew[1];
  var _search = useState('');
  var searchQuery = _search[0];
  var setSearchQuery = _search[1];
  var _filterCat = useState('');
  var filterCategory = _filterCat[0];
  var setFilterCategory = _filterCat[1];
  var _filterSt = useState('');
  var filterStatus = _filterSt[0];
  var setFilterStatus = _filterSt[1];

  var filteredVendors = useMemo(function() {
    return vendors.filter(function(v) {
      if (filterCategory && v.category_id !== filterCategory) return false;
      if (filterStatus && v.status !== filterStatus) return false;
      if (!searchQuery.trim()) return true;
      var q = searchQuery.toLowerCase();
      var catMatch = categories.find(function(c) { return c.id === v.category_id; });
      return (
        (v.name || '').toLowerCase().includes(q) ||
        (v.primary_contact || '').toLowerCase().includes(q) ||
        (v.email || '').toLowerCase().includes(q) ||
        (catMatch ? catMatch.name : '').toLowerCase().includes(q)
      );
    });
  }, [vendors, searchQuery, filterCategory, filterStatus, categories]);

  var expiredContracts = vendors.filter(function(v) { return v.status === 'active' && v.contract_end_date && new Date(v.contract_end_date) < new Date(); });
  var expiringContracts = vendors.filter(function(v) {
    if (!v.contract_end_date || v.status !== 'active') return false;
    var days = Math.ceil((new Date(v.contract_end_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 && days <= 60;
  });

  if (activeVendorId) {
    return React.createElement(VendorDetailView, {
      vendorId: activeVendorId,
      organizationId: organizationId,
      userProfile: userProfile,
      categories: categories,
      pc: pc,
      sc: sc,
      isViewer: isViewer,
      onBack: function() { setActiveVendorId(null); },
      showToast: showToast,
      signOut: signOut
    });
  }

  var handleCreateVendor = async function(vendorData) {
    try {
      var newVendor = await createVendor(vendorData);
      setShowNewVendor(false);
      showToast('Vendor created');
      try {
        var fp = 'Vendors/' + vendorData.name;
        await ensureSharePointFolder(fp);
        var subs = ['Contracts', 'Invoices', 'Correspondence', 'Insurance & Certificates'];
        for (var i = 0; i < subs.length; i++) { await ensureSharePointFolder(fp + '/' + subs[i]); }
        var spPath = '/sites/Acquisitions/Shared Documents/' + fp;
        var spUrl = 'https://walkerfuneralhomeco.sharepoint.com/sites/Acquisitions/Shared%20Documents/Forms/AllItems.aspx?id=' + encodeURIComponent(spPath) + '&viewid=658e47d3-098f-4e46-9bc4-69330c8ad528';
        await supabase.from('vendors').update({ sharepoint_folder_path: fp, sharepoint_folder_url: spUrl }).eq('id', newVendor.id);
      } catch (spErr) { console.error('SharePoint auto-create failed:', spErr); }
      if (newVendor) setActiveVendorId(newVendor.id);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  var handleDeleteVendor = async function(e, vid, vname) {
    e.stopPropagation();
    if (!confirm('Delete "' + vname + '"? This cannot be undone.')) return;
    try {
      await deleteVendor(vid);
      showToast('Vendor deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading vendors...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      {/* Page heading */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Vendors
          </h1>
          <p className="text-sm mt-1 tabular-nums" style={{ color: '#475569' }}>
            {vendors.length} vendor{vendors.length === 1 ? '' : 's'} · contracts and shared services across the portfolio
          </p>
        </div>
        {!isViewer ? (
          <button
            onClick={function() { setShowNewVendor(true); }}
            className="h-9 inline-flex items-center gap-1.5 px-3.5 text-sm font-medium rounded-lg text-white transition-all hover:shadow-md"
            style={{
              background: `linear-gradient(135deg, ${pc} 0%, #2E4B7C 100%)`,
              boxShadow: `0 1px 2px ${pc}40, 0 2px 4px ${pc}20`,
            }}
          >
            <Plus className="w-4 h-4" /> Add Vendor
          </button>
        ) : null}
      </div>

      {expiredContracts.length > 0 ? (
        <div className="mb-3 flex items-center gap-2 text-sm rounded-xl px-4 py-3"
             style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="tabular-nums">{expiredContracts.length} expired contract{expiredContracts.length !== 1 ? 's' : ''}</span>
        </div>
      ) : null}
      {expiringContracts.length > 0 ? (
        <div className="mb-3 flex items-center gap-2 text-sm rounded-xl px-4 py-3"
             style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span className="tabular-nums">{expiringContracts.length} contract{expiringContracts.length !== 1 ? 's' : ''} expiring within 60 days</span>
        </div>
      ) : null}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <VendorStat label="Active Vendors" accent="#1A3665"
          value={String(vendors.filter(function(v) { return v.status === 'active'; }).length)} />
        <VendorStat label="Pending" accent="#D97706"
          value={String(vendors.filter(function(v) { return v.status === 'pending'; }).length)} />
        <VendorStat label="Annual Contract Value" accent="#059669"
          value={'$' + vendors.filter(function(v) { return v.status === 'active' && v.contract_value; }).reduce(function(s, v) { return s + Number(v.contract_value); }, 0).toLocaleString()} />
        <VendorStat label="Categories Covered" accent="#64748B"
          value={String(new Set(vendors.filter(function(v) { return v.status === 'active'; }).map(function(v) { return v.category_id; }).filter(Boolean)).size)} />
      </div>

      {/* Filter bar */}
      <div
        className="mb-4 rounded-2xl px-4 py-3 flex gap-3 items-center flex-wrap"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          border: '1px solid #E5E9F0',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
        }}
      >
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} />
          <input
            value={searchQuery}
            onChange={function(e) { setSearchQuery(e.target.value); }}
            placeholder="Search vendors…"
            className="w-full h-9 pl-9 pr-3 text-sm rounded-lg focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          />
        </div>
        <select
          value={filterCategory}
          onChange={function(e) { setFilterCategory(e.target.value); }}
          className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none"
          style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
        >
          <option value="">All Categories</option>
          {categories.map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
        </select>
        <select
          value={filterStatus}
          onChange={function(e) { setFilterStatus(e.target.value); }}
          className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none"
          style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Inactive</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      {/* Vendor list */}
      <div className="rounded-2xl overflow-hidden"
           style={{
             backgroundColor: '#ffffff',
             border: '1px solid #E5E9F0',
             boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
           }}>
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.1em]"
             style={{ backgroundColor: '#FAFBFD', borderBottom: '1px solid #F0F2F6', color: '#94A3B8' }}>
          <div className="col-span-3">Vendor</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Contact</div>
          <div className="col-span-2">Contract</div>
          <div className="col-span-1 text-right">Value</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1"></div>
        </div>

        {filteredVendors.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {searchQuery || filterCategory || filterStatus ? 'No vendors match your filters.' : 'No vendors yet. Click "Add Vendor" to get started.'}
          </div>
        ) : (
          filteredVendors.map(function(vendor) {
            var st = STATUS_CONFIG[vendor.status] || STATUS_CONFIG.active;
            var isExp = vendor.contract_end_date && new Date(vendor.contract_end_date) < new Date();
            var isExpSoon = vendor.contract_end_date && !isExp && Math.ceil((new Date(vendor.contract_end_date) - new Date()) / 86400000) <= 60;
            var cn = '';
            var fc = categories.find(function(c) { return c.id === vendor.category_id; });
            if (fc) cn = fc.name;

            return (
              <div key={vendor.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors items-center" onClick={function() { setActiveVendorId(vendor.id); }}>
                <div className="col-span-3">
                  <div className="font-medium">{String(vendor.name)}</div>
                  {vendor.city && vendor.state ? <div className="text-xs text-gray-400">{String(vendor.city) + ', ' + String(vendor.state)}</div> : null}
                </div>
                <div className="col-span-2">
                  {cn ? <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{String(cn)}</span> : null}
                </div>
                <div className="col-span-2 text-sm">
                  <div className="truncate">{vendor.primary_contact ? String(vendor.primary_contact) : '\u2014'}</div>
                  {vendor.email ? <div className="text-xs text-gray-400 truncate">{String(vendor.email)}</div> : null}
                </div>
                <div className="col-span-2 text-sm">
                  {vendor.contract_end_date ? (
                    <div className={'flex items-center gap-1 ' + (isExp ? 'text-red-600 font-medium' : isExpSoon ? 'text-yellow-600' : 'text-gray-600')}>
                      <Calendar className="w-3 h-3" />
                      {String((isExp ? 'Expired ' : 'Ends ') + new Date(vendor.contract_end_date).toLocaleDateString())}
                    </div>
                  ) : <span className="text-gray-400 text-xs">No contract date</span>}
                </div>
                <div className="col-span-1 text-sm">
                  {vendor.contract_value ? <span className="font-medium">{'$' + Number(vendor.contract_value).toLocaleString()}</span> : <span className="text-gray-400">{'\u2014'}</span>}
                </div>
                <div className="col-span-1">
                  <span className={'text-xs px-2 py-0.5 rounded ' + st.color}>{String(st.label)}</span>
                </div>
                <div className="col-span-1 flex justify-end">
                  {!isViewer ? (
                    <button onClick={function(e) { handleDeleteVendor(e, vendor.id, vendor.name); }} className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors" title="Delete vendor">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showNewVendor ? <NewVendorModal categories={categories} pc={pc} onSave={handleCreateVendor} onClose={function() { setShowNewVendor(false); }} /> : null}
    </div>
  );
}

// Reusable stat tile matching the app-wide pattern
function VendorStat({ label, value, accent }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>{label}</div>
      <div className="text-xl font-semibold mt-1 tabular-nums leading-none" style={{ color: accent || '#0F172A', letterSpacing: '-0.015em' }}>
        {value}
      </div>
    </div>
  );
}

function NewVendorModal({ categories, pc, onSave, onClose }) {
  var colors = useDisplayColors(pc, null); var displayPrimary = colors.displayPrimary; var displaySecondary = colors.displaySecondary; var displayNavy = colors.displayNavy; var displayGreen = colors.displayGreen;
  var _form = useState({
    name: '', category_id: '', status: 'active', primary_contact: '', email: '', phone: '', website: '',
    address: '', city: '', state: '', zip: '', contract_start_date: '', contract_end_date: '',
    contract_value: '', payment_terms: '', auto_renews: false, renewal_notice_days: '', notes: '', description: ''
  });
  var form = _form[0];
  var setForm = _form[1];
  var _saving = useState(false);
  var saving = _saving[0];
  var setSaving = _saving[1];

  var handleSave = async function() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      var data = Object.assign({}, form);
      if (!data.category_id) data.category_id = null;
      if (!data.contract_value) data.contract_value = null;
      else data.contract_value = parseFloat(data.contract_value);
      if (!data.renewal_notice_days) data.renewal_notice_days = null;
      else data.renewal_notice_days = parseInt(data.renewal_notice_days);
      if (!data.contract_start_date) data.contract_start_date = null;
      if (!data.contract_end_date) data.contract_end_date = null;
      await onSave(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  var u = function(f, v) { setForm(function(p) { var n = Object.assign({}, p); n[f] = v; return n; }); };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold" style={{ color: displayPrimary }}>New Vendor</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className="block text-xs mb-1 font-medium">Vendor Name *</label><input value={form.name} onChange={function(e) { u('name', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" autoFocus /></div>
            <div className="col-span-2"><label className="block text-xs mb-1">Description</label><input value={form.description} onChange={function(e) { u('description', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="Brief description..." /></div>
            <div><label className="block text-xs mb-1">Category</label><select value={form.category_id} onChange={function(e) { u('category_id', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm"><option value="">Select...</option>{categories.map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}</select></div>
            <div><label className="block text-xs mb-1">Status</label><select value={form.status} onChange={function(e) { u('status', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm"><option value="active">Active</option><option value="pending">Pending</option><option value="inactive">Inactive</option><option value="terminated">Terminated</option></select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs mb-1">Primary Contact</label><input value={form.primary_contact} onChange={function(e) { u('primary_contact', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs mb-1">Email</label><input value={form.email} onChange={function(e) { u('email', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs mb-1">Phone</label><input value={form.phone} onChange={function(e) { u('phone', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs mb-1">Website</label><input value={form.website} onChange={function(e) { u('website', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
          </div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-6"><label className="block text-xs mb-1">Street</label><input value={form.address} onChange={function(e) { u('address', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div className="col-span-3"><input value={form.city} onChange={function(e) { u('city', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="City" /></div>
            <div className="col-span-1"><input value={form.state} onChange={function(e) { u('state', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="ST" maxLength={2} /></div>
            <div className="col-span-2"><input value={form.zip} onChange={function(e) { u('zip', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="ZIP" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs mb-1">Contract Start</label><input type="date" value={form.contract_start_date} onChange={function(e) { u('contract_start_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs mb-1">Contract End</label><input type="date" value={form.contract_end_date} onChange={function(e) { u('contract_end_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs mb-1">Annual Value ($)</label><input type="number" step="0.01" value={form.contract_value} onChange={function(e) { u('contract_value', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs mb-1">Payment Terms</label><input value={form.payment_terms} onChange={function(e) { u('payment_terms', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g., Net 30" /></div>
          </div>
          <div><label className="block text-xs mb-1">Notes</label><textarea value={form.notes} onChange={function(e) { u('notes', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" rows={3} /></div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50 text-sm">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.name.trim()} className="px-4 py-2 text-white rounded hover:opacity-90 text-sm disabled:opacity-50 flex items-center gap-2" style={{ backgroundColor: pc }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {saving ? 'Creating...' : 'Create Vendor'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VendorDetailView(props) {
  var colors = useDisplayColors(props.pc, props.sc); var displayPrimary = colors.displayPrimary; var displaySecondary = colors.displaySecondary; var displayNavy = colors.displayNavy; var displayGreen = colors.displayGreen;
  var vendorId = props.vendorId;
  var organizationId = props.organizationId;
  var userProfile = props.userProfile;
  var categories = props.categories;
  var pc = props.pc;
  var sc = props.sc;
  var isViewer = props.isViewer;
  var onBack = props.onBack;
  var showToast = props.showToast;
  var signOut = props.signOut;

  var hook = useVendor(vendorId);
  var vendor = hook.vendor;
  var contacts = hook.contacts;
  var notes = hook.notes;
  var vLoading = hook.loading;
  var updateVendor = hook.updateVendor;
  var addContact = hook.addContact;
  var deleteContact = hook.deleteContact;
  var addNote = hook.addNote;
  var deleteNote = hook.deleteNote;

  var folderHook = useVendorFolderTemplates(organizationId);
  var folderTemplates = folderHook.folders;
  var foldersLoading = folderHook.loading;
  
  var _editing = useState(false);
  var editing = _editing[0];
  var setEditing = _editing[1];
  var _editForm = useState({});
  var editForm = _editForm[0];
  var setEditForm = _editForm[1];
  var _newNote = useState('');
  var newNote = _newNote[0];
  var setNewNote = _newNote[1];
  var _showAdd = useState(false);
  var showAddContact = _showAdd[0];
  var setShowAddContact = _showAdd[1];
  var _nc = useState({ name: '', title: '', email: '', phone: '', is_primary: false });
  var newContact = _nc[0];
  var setNewContact = _nc[1];
  var _cf = useState(false);
  var creatingFolder = _cf[0];
  var setCreatingFolder = _cf[1];
  var _se = useState(false);
  var savingEdit = _se[0];
  var setSavingEdit = _se[1];

  useEffect(function() {
    if (vendor) {
      setEditForm({
        name: vendor.name || '', category_id: vendor.category_id || '', status: vendor.status || 'active',
        primary_contact: vendor.primary_contact || '', email: vendor.email || '', phone: vendor.phone || '',
        website: vendor.website || '', address: vendor.address || '', city: vendor.city || '',
        state: vendor.state || '', zip: vendor.zip || '', contract_start_date: vendor.contract_start_date || '',
        contract_end_date: vendor.contract_end_date || '', contract_value: vendor.contract_value || '',
        payment_terms: vendor.payment_terms || '', auto_renews: vendor.auto_renews || false,
        renewal_notice_days: vendor.renewal_notice_days || '', notes: vendor.notes || '', description: vendor.description || '',
      });
    }
  }, [vendor]);

  var _uf = useState(false);
  var uploadingFile = _uf[0];
  var setUploadingFile = _uf[1];
  var _vf = useState([]);
  var vendorFiles = _vf[0];
  var setVendorFiles = _vf[1];
  var _sf = useState('');
  var selectedSubfolder = _sf[0];
  var setSelectedSubfolder = _sf[1];
  // spTree: array of { name, webUrl, files: [{name, webUrl, size, modified}] }
  var _spTree = useState([]);
  var spTree = _spTree[0];
  var setSpTree = _spTree[1];
  // rootFiles: files sitting directly in the vendor root folder
  var _rootFiles = useState([]);
  var rootFiles = _rootFiles[0];
  var setRootFiles = _rootFiles[1];
  var _spLoad = useState(false);
  var spLoading = _spLoad[0];
  var setSpLoading = _spLoad[1];
  var _expanded = useState({});
  var expandedFolders = _expanded[0];
  var setExpandedFolders = _expanded[1];

  var refreshSpContents = function() {
    if (!vendor || !vendor.sharepoint_folder_path) return;
    setSpLoading(true);
    listSharePointFolder(vendor.sharepoint_folder_path).then(function(res) {
      if (!res || !res.items) return;
      var folders = res.items.filter(function(item) { return !!item.folder; });
      var files = res.items.filter(function(item) { return !!item.file; });
      setRootFiles(files.map(function(f) { return { name: f.name, webUrl: f.webUrl, size: f.size, modified: f.lastModifiedDateTime }; }));
      // Fetch each subfolder's contents in parallel
      var folderMeta = folders.map(function(f) { return { name: f.name, webUrl: f.webUrl, files: [], loading: true }; });
      setSpTree(folderMeta);
      // Expand all folders by default
      var exp = {};
      folders.forEach(function(f) { exp[f.name] = true; });
      setExpandedFolders(exp);
      Promise.all(folders.map(function(folder) {
        var subPath = vendor.sharepoint_folder_path + '/' + folder.name;
        return listSharePointFolder(subPath).then(function(subRes) {
          var subFiles = (subRes && subRes.items) ? subRes.items.filter(function(i) { return !!i.file; }) : [];
          return { name: folder.name, webUrl: folder.webUrl, files: subFiles.map(function(f) { return { name: f.name, webUrl: f.webUrl, size: f.size, modified: f.lastModifiedDateTime }; }) };
        }).catch(function() { return { name: folder.name, webUrl: folder.webUrl, files: [] }; });
      })).then(function(results) {
        setSpTree(results);
      }).finally(function() { setSpLoading(false); });
    }).catch(function(err) {
      console.error('Failed to list SP folder:', err);
      setSpLoading(false);
    });
  };

  useEffect(function() {
    if (!vendorId) return;
    supabase.from('vendor_files').select('*').eq('vendor_id', vendorId).order('created_at', { ascending: false })
      .then(function(res) { if (res.data) setVendorFiles(res.data); });
  }, [vendorId]);

  useEffect(function() {
    refreshSpContents();
  }, [vendor && vendor.sharepoint_folder_path]);

  if (vLoading || !vendor) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading vendor...</p>
      </div>
    );
  }

  var handleVendorFileUpload = async function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file || !vendor) return;
    setUploadingFile(true);
    try {
      var storagePath = organizationId + '/vendors/' + vendorId + '/' + Date.now() + '-' + file.name;
      var uploadRes = await supabase.storage.from('deal-documents').upload(storagePath, file);
      if (uploadRes.error) throw uploadRes.error;
      await supabase.from('vendor_files').insert({
        vendor_id: vendorId, organization_id: organizationId,
        original_name: file.name, storage_path: storagePath,
        size_bytes: file.size, mime_type: file.type,
        subfolder: selectedSubfolder || null,
        uploaded_by: userProfile && userProfile.id,
        uploaded_by_name: (userProfile && userProfile.full_name) || 'Unknown'
      });
      if (vendor.sharepoint_folder_path) {
        try {
          var targetFolder = vendor.sharepoint_folder_path;
          if (selectedSubfolder) targetFolder = vendor.sharepoint_folder_path + '/' + selectedSubfolder;
          await ensureSharePointFolder(targetFolder);
          var ab = await file.arrayBuffer();
          var b64 = btoa(new Uint8Array(ab).reduce(function(d, b) { return d + String.fromCharCode(b); }, ''));
          var sess = await supabase.auth.getSession();
          var token = sess.data.session && sess.data.session.access_token;
          await fetch(supabase.supabaseUrl + '/functions/v1/sharepoint-sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token, 'apikey': supabase.supabaseKey },
            body: JSON.stringify({ action: 'upload-file', folderPath: targetFolder, fileName: file.name, fileContentBase64: b64, mimeType: file.type })
          });
        } catch (spErr) { console.error('SharePoint sync failed:', spErr); }
      }
      showToast('File uploaded' + (selectedSubfolder ? ' to ' + selectedSubfolder : ''));
      supabase.from('vendor_files').select('*').eq('vendor_id', vendorId).order('created_at', { ascending: false })
        .then(function(res) { if (res.data) setVendorFiles(res.data); });
      // Refresh the SP tree so the new file appears
      refreshSpContents();
    } catch (err) { showToast(err.message, 'error'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  var handleSaveEdit = async function() {
    setSavingEdit(true);
    try {
      var data = Object.assign({}, editForm);
      if (!data.category_id) data.category_id = null;
      if (!data.contract_value) data.contract_value = null;
      else data.contract_value = parseFloat(data.contract_value);
      if (!data.renewal_notice_days) data.renewal_notice_days = null;
      else data.renewal_notice_days = parseInt(data.renewal_notice_days);
      if (!data.contract_start_date) data.contract_start_date = null;
      if (!data.contract_end_date) data.contract_end_date = null;
      await updateVendor(data);
      setEditing(false);
      showToast('Vendor updated');
    } catch (err) { showToast(err.message, 'error'); }
    finally { setSavingEdit(false); }
  };

  var handleCreateSharePointFolder = async function() {
    setCreatingFolder(true);
    try {
      var fp = 'Vendors/' + vendor.name;
      await ensureSharePointFolder(fp);
      var subs = getVendorFolderPaths(folderTemplates);
      if (subs.length === 0) {
        subs = ['Contracts', 'Invoices', 'Correspondence', 'Insurance & Certificates'];
      }
      for (var i = 0; i < subs.length; i++) { await ensureSharePointFolder(fp + '/' + subs[i]); }
      var spPath = '/sites/Acquisitions/Shared Documents/' + fp;
      var spUrl = 'https://walkerfuneralhomeco.sharepoint.com/sites/Acquisitions/Shared%20Documents/Forms/AllItems.aspx?id=' + encodeURIComponent(spPath) + '&viewid=658e47d3-098f-4e46-9bc4-69330c8ad528';
      await updateVendor({ sharepoint_folder_path: fp, sharepoint_folder_url: spUrl });
      showToast('SharePoint folder created');
    } catch (err) { showToast('SharePoint error: ' + err.message, 'error'); }
    finally { setCreatingFolder(false); }
  };

  var handleAddNote = async function() {
    if (!newNote.trim()) return;
    try {
      var un = (userProfile && userProfile.full_name) || (userProfile && userProfile.email) || 'Unknown';
      await addNote(newNote.trim(), un);
      setNewNote('');
      showToast('Note added');
    } catch (err) { showToast(err.message, 'error'); }
  };

  var handleAddContactFn = async function() {
    if (!newContact.name.trim()) return;
    try {
      await addContact(newContact);
      setNewContact({ name: '', title: '', email: '', phone: '', is_primary: false });
      setShowAddContact(false);
      showToast('Contact added');
    } catch (err) { showToast(err.message, 'error'); }
  };

  var handleDeleteContactFn = async function(cid) {
    if (!confirm('Remove this contact?')) return;
    try { await deleteContact(cid); showToast('Contact removed'); }
    catch (err) { showToast(err.message, 'error'); }
  };

  var status = STATUS_CONFIG[vendor.status] || STATUS_CONFIG.active;
  var categoryName = '';
  var foundCat = categories.find(function(c) { return c.id === vendor.category_id; });
  if (foundCat) categoryName = foundCat.name;
  var u = function(f, v) { setEditForm(function(p) { var n = Object.assign({}, p); n[f] = v; return n; }); };

  return (
    <div className="w-full p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button onClick={onBack} className="hover:bg-gray-100 p-1 rounded"><ArrowLeft className="w-5 h-5" style={{ color: displaySecondary }} /></button>
            <h1 className="text-2xl font-bold" style={{ color: displayPrimary }}>{String(vendor.name)}</h1>
            <span className={'text-xs px-2 py-0.5 rounded ' + status.color}>{String(status.label)}</span>
            {categoryName ? <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{String(categoryName)}</span> : null}
          </div>
          {vendor.description ? <p className="text-sm ml-8" style={{ color: displaySecondary }}>{String(vendor.description)}</p> : null}
        </div>
        <div className="flex gap-2">
          {!isViewer && !editing ? <button onClick={function() { setEditing(true); }} className="px-3 py-2 border rounded flex items-center gap-1.5 text-sm hover:bg-gray-50"><Edit2 className="w-4 h-4" /> Edit</button> : null}
          <button onClick={signOut} className="px-3 py-2 bg-gray-100 rounded flex items-center gap-1.5 text-sm hover:bg-gray-200"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-4">
          {editing ? (
            <div className="bg-white rounded-lg shadow p-4 space-y-4">
              <h3 className="font-semibold" style={{ color: displayPrimary }}>Edit Vendor</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-xs mb-1 font-medium">Name</label><input value={editForm.name} onChange={function(e) { u('name', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                <div className="col-span-2"><label className="block text-xs mb-1">Description</label><input value={editForm.description} onChange={function(e) { u('description', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs mb-1">Category</label><select value={editForm.category_id} onChange={function(e) { u('category_id', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm"><option value="">None</option>{categories.map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}</select></div>
                <div><label className="block text-xs mb-1">Status</label><select value={editForm.status} onChange={function(e) { u('status', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm"><option value="active">Active</option><option value="pending">Pending</option><option value="inactive">Inactive</option><option value="terminated">Terminated</option></select></div>
                <div><label className="block text-xs mb-1">Contact</label><input value={editForm.primary_contact} onChange={function(e) { u('primary_contact', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs mb-1">Email</label><input value={editForm.email} onChange={function(e) { u('email', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs mb-1">Phone</label><input value={editForm.phone} onChange={function(e) { u('phone', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs mb-1">Website</label><input value={editForm.website} onChange={function(e) { u('website', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                <div className="col-span-2"><input value={editForm.address} onChange={function(e) { u('address', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="Street" /></div>
                <div><input value={editForm.city} onChange={function(e) { u('city', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="City" /></div>
                <div className="flex gap-2"><input value={editForm.state} onChange={function(e) { u('state', e.target.value); }} className="w-1/3 border rounded px-3 py-2 text-sm" placeholder="ST" maxLength={2} /><input value={editForm.zip} onChange={function(e) { u('zip', e.target.value); }} className="w-2/3 border rounded px-3 py-2 text-sm" placeholder="ZIP" /></div>
              </div>
              <div><label className="block text-xs mb-1">Notes</label><textarea value={editForm.notes} onChange={function(e) { u('notes', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" rows={3} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={function() { setEditing(false); }} className="px-4 py-2 border rounded hover:bg-gray-50 text-sm">Cancel</button>
                <button onClick={handleSaveEdit} disabled={savingEdit} className="px-4 py-2 text-white rounded hover:opacity-90 text-sm disabled:opacity-50" style={{ backgroundColor: pc }}>{savingEdit ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {vendor.primary_contact ? <div><div className="text-xs text-gray-400">Primary Contact</div><div className="text-sm font-medium">{String(vendor.primary_contact)}</div></div> : null}
                {vendor.email ? <div><div className="text-xs text-gray-400">Email</div><div className="text-sm text-blue-600">{String(vendor.email)}</div></div> : null}
                {vendor.phone ? <div><div className="text-xs text-gray-400">Phone</div><div className="text-sm text-blue-600">{String(vendor.phone)}</div></div> : null}
                {vendor.website ? <div><div className="text-xs text-gray-400">Website</div><div className="text-sm text-blue-600">{String(vendor.website)}</div></div> : null}
              </div>
              {vendor.notes ? <div className="border-t mt-4 pt-4"><div className="text-xs text-gray-400 mb-1">Notes</div><div className="text-sm whitespace-pre-wrap">{String(vendor.notes)}</div></div> : null}
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: displayPrimary }}><FileText className="w-4 h-4" /> {'Documents'}</h3>
              {vendor.sharepoint_folder_url ? (
                <div className="flex items-center gap-2">
                  <button onClick={refreshSpContents} disabled={spLoading} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Refresh">
                    <RefreshCw className={'w-3.5 h-3.5' + (spLoading ? ' animate-spin' : '')} />
                  </button>
                  <a href={String(vendor.sharepoint_folder_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline"><ExternalLink className="w-3 h-3" /> SharePoint</a>
                </div>
              ) : null}
            </div>
            {vendor.sharepoint_folder_url ? (
              <div>
                {spLoading && spTree.length === 0 ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-4 justify-center"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading contents...</div>
                ) : (
                  <div className="border rounded overflow-hidden">
                    {rootFiles.map(function(f) {
                      return (
                        <a key={'root-' + f.name} href={f.webUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-xs text-gray-700 group">
                          <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                          <span className="flex-1 truncate">{String(f.name)}</span>
                          <span className="text-gray-400">{f.modified ? new Date(f.modified).toLocaleDateString() : ''}</span>
                          <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                        </a>
                      );
                    })}
                    {spTree.map(function(folder) {
                      var isOpen = !!expandedFolders[folder.name];
                      return (
                        <div key={folder.name} className="border-b last:border-b-0">
                          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 cursor-pointer select-none"
                            onClick={function() {
                              setExpandedFolders(function(prev) {
                                var next = Object.assign({}, prev);
                                next[folder.name] = !prev[folder.name];
                                return next;
                              });
                            }}>
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                            <svg className="w-4 h-4 flex-shrink-0 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                            <span className="text-xs font-medium text-gray-700 flex-1">{String(folder.name)}</span>
                            <span className="text-xs text-gray-400">{folder.files.length > 0 ? String(folder.files.length) + (folder.files.length === 1 ? ' file' : ' files') : 'empty'}</span>
                            <a href={folder.webUrl} target="_blank" rel="noopener noreferrer" className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-blue-600 ml-1" title="Open folder in SharePoint" onClick={function(e) { e.stopPropagation(); }}>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          {isOpen ? (
                            <div className="border-t">
                              {folder.files.length === 0 ? (
                                <div className="px-8 py-2 text-xs text-gray-400 italic">Empty folder</div>
                              ) : (
                                folder.files.map(function(f) {
                                  return (
                                    <a key={f.name} href={f.webUrl} target="_blank" rel="noopener noreferrer"
                                      className="flex items-center gap-2 pl-8 pr-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-xs text-gray-700 group">
                                      <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                                      <span className="flex-1 truncate">{String(f.name)}</span>
                                      <span className="text-gray-400">{f.modified ? new Date(f.modified).toLocaleDateString() : ''}</span>
                                      <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                    </a>
                                  );
                                })
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {rootFiles.length === 0 && spTree.length === 0 && !spLoading ? (
                      <div className="px-3 py-6 text-xs text-gray-400 text-center">No files in SharePoint yet.</div>
                    ) : null}
                  </div>
                )}
                {!isViewer ? (
                  <div className="mt-3 space-y-2">
                    <select value={selectedSubfolder} onChange={function(e) { setSelectedSubfolder(e.target.value); }} className="w-full border rounded px-2 py-1.5 text-sm">
                      <option value="">Upload to root folder</option>
                      {spTree.map(function(folder) { return <option key={folder.name} value={folder.name}>{String(folder.name)}</option>; })}
                    </select>
                    <label className="px-3 py-1.5 border rounded text-sm cursor-pointer hover:bg-gray-50 inline-flex items-center gap-1.5" style={{ color: displayPrimary, borderColor: pc }}>
                      {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploadingFile ? 'Uploading...' : 'Upload File'}
                      <input type="file" className="hidden" onChange={handleVendorFileUpload} disabled={uploadingFile} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt" />
                    </label>
                  </div>
                ) : null}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-3">Create a SharePoint folder for documents.</p>
                {!isViewer ? (
                  <button onClick={handleCreateSharePointFolder} disabled={creatingFolder} className="px-3 py-2 border rounded text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50">
                    {creatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
                    {creatingFolder ? 'Creating...' : 'Create SharePoint Folder'}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="col-span-1 space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: displayPrimary }}><Users className="w-4 h-4" /> {'Contacts'} <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{String(contacts.length)}</span></h3>
              {!isViewer ? <button onClick={function() { setShowAddContact(true); }} className="text-sm flex items-center gap-1 hover:underline" style={{ color: displayPrimary }}><UserPlus className="w-3 h-3" /> Add</button> : null}
            </div>
            {showAddContact ? (
              <div className="border rounded p-3 mb-3 space-y-2 bg-gray-50">
                <div className="grid grid-cols-2 gap-2">
                  <input value={newContact.name} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { name: e.target.value }); }); }} placeholder="Name *" className="border rounded px-2 py-1.5 text-sm" autoFocus />
                  <input value={newContact.title} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { title: e.target.value }); }); }} placeholder="Title" className="border rounded px-2 py-1.5 text-sm" />
                  <input value={newContact.email} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { email: e.target.value }); }); }} placeholder="Email" className="border rounded px-2 py-1.5 text-sm" />
                  <input value={newContact.phone} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { phone: e.target.value }); }); }} placeholder="Phone" className="border rounded px-2 py-1.5 text-sm" />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={function() { setShowAddContact(false); }} className="px-2 py-1 border rounded text-xs hover:bg-gray-50">Cancel</button>
                  <button onClick={handleAddContactFn} disabled={!newContact.name.trim()} className="px-2 py-1 text-white rounded text-xs disabled:opacity-50" style={{ backgroundColor: pc }}>Add</button>
                </div>
              </div>
            ) : null}
            {contacts.length === 0 && !showAddContact ? <div className="text-sm text-gray-400 text-center py-4">No additional contacts</div> : (
              <div className="space-y-2">
                {contacts.map(function(contact) {
                  return (
                    <div key={contact.id} className="border rounded p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-sm font-medium">{String(contact.name)}</div>
                          {contact.title ? <div className="text-xs text-gray-400">{String(contact.title)}</div> : null}
                        </div>
                        {!isViewer ? <button onClick={function() { handleDeleteContactFn(contact.id); }} className="text-red-400 hover:text-red-600 p-1"><X className="w-3 h-3" /></button> : null}
                      </div>
                      <div className="flex gap-4 mt-1.5">
                        {contact.email ? <span className="text-xs text-blue-600">{String(contact.email)}</span> : null}
                        {contact.phone ? <span className="text-xs text-blue-600">{String(contact.phone)}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: displayPrimary }}><MessageSquare className="w-4 h-4" /> {'Notes'} <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{String(notes.length)}</span></h3>
            {!isViewer ? (
              <div className="flex gap-2 mb-4">
                <input value={newNote} onChange={function(e) { setNewNote(e.target.value); }} placeholder="Add a note..." className="flex-1 border rounded px-3 py-2 text-sm" onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) handleAddNote(); }} />
                <button onClick={handleAddNote} disabled={!newNote.trim()} className="px-3 py-2 text-white rounded hover:opacity-90 text-sm disabled:opacity-50" style={{ backgroundColor: pc }}>Add</button>
              </div>
            ) : null}
            {notes.length === 0 ? <div className="text-sm text-gray-400 text-center py-4">No notes yet</div> : (
              <div className="space-y-3">
                {notes.map(function(note) {
                  return (
                    <div key={note.id} className="border-l-2 pl-3 py-1" style={{ borderLeftColor: displayPrimary }}>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-400">{String(note.created_by_name || 'Unknown') + ' \u2022 ' + new Date(note.created_at).toLocaleString()}</div>
                        {!isViewer ? <button onClick={function() { deleteNote(note.id); }} className="text-red-400 hover:text-red-600 p-1"><X className="w-3 h-3" /></button> : null}
                      </div>
                      <div className="text-sm mt-0.5 whitespace-pre-wrap">{String(note.content)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
